import * as React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Zap, ShieldAlert, Users, ArrowUp, ArrowDown, Star } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { DraggableBlocksContainer } from './DraggableBlocksContainer';
import { QuickActionsBar } from './QuickActionsBar';
import { AwaitingMyActionCard } from './AwaitingMyActionCard';
import {
  GovernanceAlertsBlock,
  GovernanceTeamLoadBlock,
  GovernanceApprovalsBlock,
} from './Case360GovernancePanel';
import { usePartnerMyTeam } from '../hooks/usePartnerMyTeam';
import type { PartnerTeamMember } from '../api/dashboard.api';
import { K3RatingDialog } from './K3RatingDialog';
import { MyMattersCard } from './MyMattersCard';

const K3_RATING_META: Record<number, { bg: string; text: string; border: string; label: string }> = {
  5: { bg: 'bg-green-50',   text: 'text-green-700',   border: 'border-green-200',   label: 'Exceptional' },
  4: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Exceeds' },
  3: { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   label: 'Meets' },
  2: { bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200',  label: 'Below' },
  1: { bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200',     label: 'Poor' },
};

function formatHours(hours: number) {
  return `${hours.toFixed(1)}h`;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function AlertItem({
  severity,
  icon,
  title,
  description,
  value,
  threshold,
}: {
  severity: 'critical' | 'warning';
  icon: React.ReactNode;
  title: string;
  description: string;
  value: string;
  threshold: string;
}) {
  const isCritical = severity === 'critical';
  return (
    <div className={`flex items-center gap-3.5 px-4 py-3 rounded-lg border ${isCritical ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${isCritical ? 'bg-red-100 text-red-700' : 'bg-amber-200 text-amber-700'}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[0.875rem] font-semibold text-slate-900 mb-0.5">{title}</div>
        <div className="text-[0.8125rem] text-slate-500">{description}</div>
      </div>
      <div className={`text-lg font-bold tabular-nums whitespace-nowrap ${isCritical ? 'text-red-700' : 'text-amber-700'}`}>
        {value}<span className="text-[0.75rem] font-medium text-slate-400 ml-1">{threshold}</span>
      </div>
    </div>
  );
}

function LoadingRows({ cols }: { cols: number }) {
  return (
    <>
      {[...Array(3)].map((_, i) => (
        <tr key={i} className="border-b border-slate-100 last:border-b-0">
          {[...Array(cols)].map((__, j) => (
            <td key={j} className="px-2 py-2.5">
              <div className="h-4 bg-slate-100 rounded animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function PartnerDashboard() {
  const { t } = useTranslation('common');

  const [pendingSort, setPendingSort] = useState<'asc' | 'desc'>('desc');
  const [k3Target, setK3Target] = useState<PartnerTeamMember | null>(null);
  const isRatingDay = new Date().getDate() === 29;

  const { data: teamData, isLoading: teamLoading } = usePartnerMyTeam();

  const blocks = [
    // Case 360° (Stage 12) — each card is its own draggable block.
    { id: 'quick-actions', content: <QuickActionsBar showSearch /> },
    { id: 'awaiting-my-action', content: <AwaitingMyActionCard /> },
    { id: 'governance-alerts', content: <GovernanceAlertsBlock /> },
    { id: 'governance-team-load', content: <GovernanceTeamLoadBlock /> },
    { id: 'governance-approvals', content: <GovernanceApprovalsBlock /> },
    /* {
      id: 'alerts',
      content: (
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle size={16} className="text-slate-400" /> {t('dashboard.systemAlerts')}
            </CardTitle>
            <CardDescription>{t('dashboard.partnerAlertsDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex flex-col gap-2.5">
              <AlertItem severity="critical" icon={<Zap size={14} />} title={t('dashboard.emergencyRateAbove')} description={t('dashboard.emergencyRateDesc')} value="7.2%" threshold="/ 5%" />
              <AlertItem severity="warning" icon={<ShieldAlert size={14} />} title={t('dashboard.bypassedRateElevated')} description={t('dashboard.bypassedRateDesc')} value="4.8%" threshold="/ 3%" />
            </div>
          </CardContent>
        </Card>
      ),
    }, */
    {
      id: 'matters',
      // 2026-06-06 — reuses the lawyer dashboard's MyMattersCard (partner
      // variant: no role filter/column, matter-wide task counts, paged +10).
      content: <MyMattersCard variant="partner" />,
    },
    {
      // Keeps the historical block id so saved layouts stay valid (the
      // Approval Queue half of this block was removed 2026-06-06).
      id: 'team-queue',
      content: (
          <Card>
            <CardHeader className="pb-3 border-b border-slate-100">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users size={16} className="text-slate-400" /> {t('dashboard.myTeam')}
                </CardTitle>
                <CardDescription>{t('dashboard.myTeamDesc')}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{t('k3.rating')}</th>
                      {[t('dashboard.lawyer'), t('role'), t('dashboard.utilization'), t('dashboard.hours')].map(h => (
                        <th key={h} className="text-left px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                      ))}
                      <th className="text-left px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        <button
                          onClick={() => setPendingSort(s => s === 'asc' ? 'desc' : 'asc')}
                          className="inline-flex items-center gap-1 hover:text-slate-900 transition-colors"
                        >
                          {t('dashboard.pending')}
                          {pendingSort === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamLoading ? (
                      <LoadingRows cols={6} />
                    ) : (
                      [...(teamData?.data ?? [])]
                        .sort((a, b) => (a.lawyer_full_name ?? '').localeCompare(b.lawyer_full_name ?? ''))
                        .sort((a, b) => pendingSort === 'asc'
                          ? (a.pending_hours ?? 0) - (b.pending_hours ?? 0)
                          : (b.pending_hours ?? 0) - (a.pending_hours ?? 0)
                        )
                        .map(m => (
                        <tr key={m.lawyerId} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                          <td className="px-2 py-2">
                            {m.avg_rating != null ? (() => {
                              const meta = K3_RATING_META[Math.round(m.avg_rating)] ?? K3_RATING_META[3];
                              return (
                                <span
                                  title={meta.label}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.bg} ${meta.text} ${meta.border}`}
                                >
                                  <Star className="h-2.5 w-2.5 fill-current shrink-0" />
                                  {Math.round(m.avg_rating * 100) / 100}
                                  <span className="opacity-40 font-normal">/5</span>
                                </span>
                              );
                            })() : isRatingDay ? (
                              <button
                                type="button"
                                onClick={() => setK3Target(m)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 hover:border-violet-300 transition-colors whitespace-nowrap"
                              >
                                <Star className="h-3 w-3" />
                                {t('k3.rate')}
                              </button>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                            
                          </td>
                          <td className="px-2 py-2">
                            <Link to={`/app/users/${m.lawyerId}`} className="text-blue-600 hover:underline">
                              {m.lawyer_full_name}
                            </Link>
                          </td>
                          <td className="px-2 py-2 text-slate-500 text-[0.8125rem]">{m.lawyerRole}</td>
                          <td className="px-2 py-2 tabular-nums text-slate-900">{(m.utilization ?? 0).toFixed(1)}%</td>
                          <td className="px-2 py-2 tabular-nums text-slate-900">{formatHours(m.hours)}</td>
                          <td className="px-2 py-2 tabular-nums text-slate-500">{formatHours(m.pending_hours)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
      ),
    },
  ];

  return (
    <>
      <DraggableBlocksContainer storageKey="andersen.partner.blockOrder" blocks={blocks} />

      {k3Target && (
        <K3RatingDialog
          open={!!k3Target}
          onOpenChange={(open) => { if (!open) setK3Target(null); }}
          lawyerId={k3Target.lawyerId}
          lawyerName={k3Target.lawyer_full_name}
          onSuccess={() => setK3Target(null)}
        />
      )}
    </>
  );
}
